import React from 'react';
import PropTypes from 'prop-types';
import Config from '../../config';

// Utils
import getWindow from 'Utils/get-window';
import ObjectUtil from 'Utils/object-util';

// Components
import WebLink from './web-link';
import withT from 'ComponentLibrary/hocs/withT';

import GdprApplies from './gdpr-applies';

import connector from 'Utils/connector';
import connectFeatureFlags from 'Webapp/shared/app/connectors/connectFeatureFlags';

const DNS_LINK_INCLUDE_PROPS = ['href', 'name', 'className', 'onClick'];

function DataProtectionLink(props) {
  const { auto, label, onClick, requireGdprApplies, t, featureFlags } = props;

  const showPrivacyManager = (e) => {
    if (e) {
      e.preventDefault();
    }

    const window = getWindow();
    if (featureFlags.GOOGLE_CMP) {
      // The queue is normally installed by the googlefc bootstrap partial;
      // recreating it here covers pages rendered without JS externals.
      window.googlefc = window.googlefc || {};
      window.googlefc.callbackQueue = window.googlefc.callbackQueue || [];
      window.googlefc.callbackQueue.push(() =>
        window.googlefc.showRevocationMessage(),
      );
      return;
    }

    window._sp_?.gdpr?.loadPrivacyManagerModal?.(
      Config.SOURCEPOINT_GDPR_PRIVACY_MANAGER_ID,
    );
  };
  const handleClick = onClick
    ? (e) => {
        showPrivacyManager(e);
        onClick(e);
      }
    : showPrivacyManager;

  const linkProps = ObjectUtil.pick(
    Object.assign({}, props, { onClick: handleClick }),
    DNS_LINK_INCLUDE_PROPS,
  );

  if (auto) {
    showPrivacyManager();
  }
  // Google requires the EEA/UK re-open link be labeled "Privacy and cookie
  // settings"; non-compliance can get the consent message disabled (G5). The
  // mandated label therefore OVERRIDES a caller-supplied one rather than
  // deferring to it: privacy-settings.tsx passes the words lifted out of the
  // translated `privacy_settings` sentence ("Privacy Manager"), which would
  // otherwise win and leave the link mislabelled on the one page whose whole
  // purpose is privacy choices.
  const mandatedLabel = featureFlags.GOOGLE_CMP
    ? t('privacy_and_cookie_settings')
    : null;
  const words = mandatedLabel || label || t('data_protection');
  // Same reason as `do-not-sell-link.js`: no caller supplies an href, so as a
  // WebLink this rendered `<a href="/">` and a cmd-click opened the homepage.
  const link = linkProps.href ? (
    <WebLink {...linkProps}>{words}</WebLink>
  ) : (
    <button type="button" className={linkProps.className} onClick={handleClick}>
      {words}
    </button>
  );

  if (!requireGdprApplies) {
    return link;
  }

  // Where GDPR does not apply there is nothing to re-open: Google's
  // showRevocationMessage() is specified to do nothing, and the Sourcepoint
  // branch is optional-chained, so the click is silently inert. Rendering the
  // same words as text keeps the surrounding sentence intact and stops
  // offering a control that cannot act. Callers that can drop the element
  // entirely, like the footer, wrap themselves in GdprApplies instead.
  return (
    <GdprApplies>
      {(applies) => (applies ? link : <span>{words}</span>)}
    </GdprApplies>
  );
}

DataProtectionLink.propTypes = {
  auto: PropTypes.bool,
  label: PropTypes.string,
  onClick: PropTypes.func,
  requireGdprApplies: PropTypes.bool,
  t: PropTypes.func.isRequired,
  featureFlags: PropTypes.shape({
    GOOGLE_CMP: PropTypes.bool,
  }).isRequired,
};

DataProtectionLink.defaultProps = {
  auto: false,
  onClick: null,
  requireGdprApplies: false,
};

export default connector(connectFeatureFlags)(withT(DataProtectionLink));
